import { firestore } from 'firebase';
import sort from 'fast-sort';
import dayjs from 'dayjs';

import DatabaseAdapter from 'src/firebase/db/DatabaseAdapter';
import ShopsDB, { ShopEntity } from 'src/firebase/db/Shops';
import UsersDB from 'src/firebase/db/Users';
import BakedBreadsDB, {
  BakedBreadEntity,
  bakedBreadInitialEntity,
} from 'src/firebase/db/BakedBreads';
import OrderBreadsDB, {
  OrderBreadEntity,
  OrderBreadProps,
  orderBreadInitialEntity,
} from 'src/firebase/db/OrderBreads';
import OrdersDB, { OrderEntity } from 'src/firebase/db/Orders';
import NotificationTemplatesDB, {
  NotificationTemplateEntity,
  NotificationTemplateBakedBreadEntity,
  notificationTemplateInitialEntity,
} from 'src/firebase/db/NotificationTemplates';

import {
  excludeByKeys,
  copyDateOnlyYYYYMMDD,
  copyTimeOnlyHHmmss,
  formatDateToDate,
  toaster,
} from 'src/helpers/UtilityHelper';
import { isDeletableScheduledTime } from 'src/helpers/NotificationHelper';
import { ReferenceError, InternalError } from 'src/firebase/db/ApplicationError';

export const notificationInitialEntity = {
  id: '',
  needs_template: false,
  template_title: '',
  shop_id: '',
  shop_name: '',
  shop_address: '',
  published_time: undefined as Date | undefined,
  scheduled_time: undefined as Date | undefined,
  can_order_time: undefined as Date | undefined,
  last_pickup_time: undefined as Date | undefined,
  last_pickup_days: [] as Date[] | undefined[],
  pickup_supplement: '',
  is_draft: false,
  is_published: false,
  is_notified: false,
  is_reservation: false,
  notified_at: undefined as Date | undefined,
  created_at: {} as firestore.Timestamp,
  updated_at: {} as firestore.Timestamp,
  order_breads: [] as OrderBreadEntity[],
  orders: [] as OrderEntity[],
};
const extendProps = {
  ...notificationInitialEntity,
  order_breads: [] as OrderBreadProps[],
};
export type NotificationEntity = typeof notificationInitialEntity;
export type NotificationProps = typeof extendProps;
export const notificationEntityKeys = (Object.keys(
  notificationInitialEntity,
) as unknown) as (keyof NotificationEntity)[];

class NotificationsDB extends DatabaseAdapter {
  constructor(props: any) {
    super(props);
    const keys = excludeByKeys(notificationEntityKeys, ['orders']);
    this.setSchemaKeys(keys);
  }

  // 店舗に紐づく通知を全て取得する
  getNotifications(shopId: string) {
    const notifications: NotificationEntity[] = [];
    return this.getConnection()
      .where('shop_id', '==', shopId)
      .get()
      .then(async (snapshot) => {
        if (snapshot.empty) {
          console.log('No matching documents.');
          throw new ReferenceError('焼き上がりをお知らせできるパンが存在しません');
        }
        await Promise.all(
          snapshot.docs.map(async (doc) => {
            const data = doc.data();
            notifications.push({
              ...data,
            } as NotificationEntity);
          }),
        );
        return this.formatSuccess<NotificationEntity[]>(notifications);
      })
      .catch((err) => {
        if (err instanceof ReferenceError) {
          throw err;
        }
        throw new InternalError(err.message);
      });
  }

  // 過去に通知を飛ばしたことがあるかチェックする
  hasNotifications(shopId: string) {
    return this.getConnection()
      .where('shop_id', '==', shopId)
      .get()
      .then(async (snapshot) => {
        return this.formatSuccess(!snapshot.empty);
      })
      .catch((err) => {
        if (err instanceof ReferenceError) {
          throw err;
        }
        throw new InternalError(err.message);
      });
  }

  // 予約商品をIDで取得
  getReservation(id: string, isAdmin?: boolean) {
    return this.getDocumentRef(id)
      .get()
      .then(async (doc) => {
        if (!doc.exists) {
          console.log('No matching documents.');
          throw new ReferenceError('予約販売用のデータが存在しません。');
        }
        const notification = (await doc.data()) as NotificationEntity;
        const { shop_id } = notification;
        const { data: orderBreads } = await OrderBreadsDB.getNotificationOrderBreads(
          shop_id,
          doc.ref,
          isAdmin,
        );

        // パンのデータを取得
        const orderBread = orderBreads.length ? orderBreads[0] : orderBreadInitialEntity;
        const bakedBread = await BakedBreadsDB.getDocumentRef(orderBread.bread_id)
          .get()
          .then(async (doc) => (await doc.data()) as BakedBreadEntity)
          .catch((err) => {
            console.error(err);
            return bakedBreadInitialEntity;
          });

        return this.formatSuccess<{
          notification: NotificationEntity;
          orderBread: OrderBreadEntity;
          bakedBread: BakedBreadEntity;
        }>({
          notification: {
            ...notification,
            order_breads: orderBreads,
          },
          orderBread,
          bakedBread,
        });
      })
      .catch((err) => {
        if (err instanceof ReferenceError) {
          throw err;
        }
        throw new InternalError(err.message);
      });
  }

  getDayReservation(payload: { id: string; shopId?: string; isAdmin?: boolean }) {
    const { id, shopId, isAdmin } = payload;
    return this.getDocumentRef(id)
      .get()
      .then(async (doc) => {
        if (!doc.exists) {
          console.log('No matching documents.');
          throw new ReferenceError('予約販売用のデータが存在しません。');
        }
        const notification = (await doc.data()) as NotificationProps;
        const { shop_id: notificationShopId } = notification;

        if (!isAdmin && (!shopId || notificationShopId !== shopId)) {
          throw new ReferenceError('店舗IDが不正です。');
        }

        // 紐づくパンのデータを取得
        let orderBreads: OrderBreadProps[] = [];
        try {
          const { data: order_breads } = await OrderBreadsDB.getNotificationOrderBreads(
            notificationShopId,
            doc.ref,
            isAdmin,
          );
          orderBreads = order_breads;
        } catch (e) {
          // データが存在しない場合、throwされたエラーを握り潰して空配列を返す
          orderBreads = [];
          console.error(e);
        }

        // 紐づく注文データを取得
        let orders: OrderEntity[] = [];
        try {
          const { data } = await OrdersDB.getNotificationOrders(notificationShopId, doc.ref);
          orders = data;
        } catch (e) {
          // データが存在しない場合、throwされたエラーを握り潰して空配列を返す
          orders = [];
        }
        return this.formatSuccess<NotificationProps>({
          ...notification,
          order_breads: orderBreads,
          orders,
        });
      })
      .catch((err) => {
        if (err instanceof ReferenceError) {
          throw err;
        }
        throw new InternalError(err.message);
      });
  }

  // 店舗に紐づく予約商品を取得
  getReservations(paloyad: { shopId: string; isNotified?: boolean; isAdmin?: boolean }) {
    const { shopId, isNotified, isAdmin } = paloyad;
    let query = this.getConnection().where('is_reservation', '==', true);
    if (isNotified) {
      query = query.where('is_notified', '==', true);
    }
    if (!isAdmin) {
      query = query.where('shop_id', '==', shopId);
    }
    return query
      .get()
      .then(async (snapshot) => {
        if (snapshot.empty) {
          console.log('No matching documents.');
          throw new ReferenceError('予約販売用のデータが存在しません。');
        }
        const notifications = await Promise.all(
          snapshot.docs.map(async (doc) => {
            // 通知明細のデータをセット
            const notification = (await doc.data()) as NotificationEntity;

            const { data: order_breads } = await OrderBreadsDB.getNotificationOrderBreads(
              shopId,
              doc.ref,
              isAdmin,
            );
            return {
              ...notification,
              order_breads,
            };
          }),
        );
        return this.formatSuccess<NotificationEntity[]>(notifications);
      })
      .catch((err) => {
        console.error(err);
        if (err instanceof ReferenceError) {
          throw err;
        }
        throw new InternalError(err.message);
      });
  }

  async getDayNotifications(
    shopId: string,
    startDate: Date,
    endDate: Date,
    isReservation: boolean,
    noDetails?: boolean,
  ) {
    let notifications: NotificationProps[] = [];

    const query = this.getConnection()
      .where('shop_id', '==', shopId)
      .where('scheduled_time', '>=', startDate)
      .where('scheduled_time', '<=', endDate)
      .orderBy('scheduled_time', 'desc');

    // 現在のfirestoreだとnot-inが使えないので、直接変数でフィルタリングする必要あり
    if (isReservation) {
      query.where('is_reservation', '==', true);
    }

    return await query
      .get()
      .then(async (snapshot) => {
        // 通知が存在しない場合
        if (snapshot.empty) {
          throw new ReferenceError('配信した通知がありません。');
        }

        // 取得した通知に詳細情報を付与する
        await Promise.all(
          snapshot.docs.map(async (notificationDoc) => {
            const notification = (await notificationDoc.data()) as NotificationProps;
            const { shop_id: shopId } = notification;

            // 紐づくパンのデータを取得
            let orderBreads: OrderBreadProps[] = [];
            if (!noDetails) {
              try {
                const { data: order_breads } = await OrderBreadsDB.getNotificationOrderBreads(
                  shopId,
                  notificationDoc.ref,
                );
                orderBreads = order_breads;
              } catch (e) {
                // データが存在しない場合、throwされたエラーを握り潰して空配列を返す
                orderBreads = [];
                console.error(e);
              }
            }

            // 紐づく注文データを取得
            let orders: OrderEntity[] = [];
            if (!noDetails) {
              try {
                const { data } = await OrdersDB.getNotificationOrders(shopId, notificationDoc.ref);
                orders = data;
              } catch (e) {
                // データが存在しない場合、throwされたエラーを握り潰して空配列を返す
                orders = [];
              }
            }

            notifications.push({
              ...notification,
              order_breads: orderBreads,
              orders,
            } as NotificationProps);
          }),
        );

        if (!isReservation) {
          notifications = notifications.filter((v) => v.is_reservation !== true);
        }

        // Firestoreのソートが効いてないので修正
        const results = sort(notifications).by([
          { desc: (v) => formatDateToDate(v.scheduled_time).getTime() },
        ]);

        return this.formatSuccess<NotificationEntity[]>(results);
      })
      .catch((err) => {
        if (err instanceof ReferenceError) {
          throw err;
        }
        throw new InternalError(err.message);
      });
  }

  // テンプレートを通知に変換
  async getTemplatesNotifications(templateIds: string[]) {
    // テンプレートを取得
    const templates = await NotificationTemplatesDB.getConnection()
      .where('id', 'in', templateIds)
      .get()
      .then((snapshot) =>
        Promise.all(
          snapshot.docs.map(async (doc) => (await doc.data()) as NotificationTemplateEntity),
        ),
      );

    return Promise.all(
      templates.map(async (template) => {
        const { scheduled_time, can_order_time, last_pickup_time, name } = template;
        const scheduledTime = copyTimeOnlyHHmmss(formatDateToDate(scheduled_time));
        const canOrderTime = copyTimeOnlyHHmmss(formatDateToDate(can_order_time));
        const lastPickupTime = copyTimeOnlyHHmmss(formatDateToDate(last_pickup_time));
        return ShopsDB.getDocumentRef(template.shop_id)
          .get()
          .then(async (doc) => (await doc.data()) as ShopEntity)
          .then((shop) =>
            Promise.all(
              template.baked_breads.map(async (v) => {
                const bakedBread = (await (await v.ref.get()).data()) as BakedBreadEntity;
                // 通知明細を作成
                return {
                  ...orderBreadInitialEntity,
                  selected: true,
                  bread_id: v.ref.id,
                  shop_id: template.shop_id,
                  shop_name: shop.name,
                  shop_address: shop.address,
                  image: bakedBread.image,
                  name: bakedBread.name,
                  price: bakedBread.price,
                  description: bakedBread.description,
                  width: bakedBread.width || '',
                  depth: bakedBread.depth || '',
                  height: bakedBread.height || '',
                  best_before: bakedBread.best_before || '',
                  is_reservation: false,
                  scheduled_time: scheduledTime,
                  can_order_time: canOrderTime,
                  last_pickup_time: lastPickupTime,
                  stock: v.stock,
                  maximum_order: v.maximum_order,
                } as OrderBreadEntity;
              }),
            ),
          )
          .then((orderBreads) => ({
            // 通知を作成
            ...notificationInitialEntity,
            needs_template: false,
            template_title: name,
            shop_id: template.shop_id,
            shop_name: orderBreads[0].shop_name || '',
            shop_address: orderBreads[0].shop_address || '',
            published_time: scheduledTime,
            scheduled_time: scheduledTime,
            can_order_time: canOrderTime,
            last_pickup_time: lastPickupTime,
            pickup_supplement: template.pickup_supplement,
            order_breads: orderBreads,
          }));
      }),
    )
      .then((notifications) => {
        const sorted = sort(notifications).by([
          { desc: (v) => (v.published_time ? v.published_time.getTime() : false) },
          { desc: (v) => (v.scheduled_time ? v.scheduled_time.getTime() : false) },
          { desc: (v) => formatDateToDate(v.updated_at) },
        ]);
        return this.formatSuccess<NotificationEntity[]>(sorted);
      })
      .catch((err) => {
        console.log(err);
        if (err instanceof ReferenceError) {
          throw err;
        }
        throw new InternalError(err.message);
      });
  }

  async create(query: NotificationEntity) {
    const filteredQuery = this.filterQuery(query);

    const orderBreads = filteredQuery.order_breads?.filter((v) => v.selected) || [];
    const orderBreadRefs = [] as firestore.DocumentReference[];

    const requestQuery = {
      ...filteredQuery,
      last_pickup_days: [],
      notified_at: null, // Firebase側で日付のundefinedは禁止されているため
      created_at: this.getServerTimeStamp(),
      updated_at: this.getServerTimeStamp(),
    };

    // notificationに保存させたくないキーを保持しつつ削除（setSchemaKeysでうまく制御できなかったので...）
    const { needs_template, template_title } = requestQuery;
    delete requestQuery.needs_template;
    delete requestQuery.template_title;

    // delete requestQuery.last_pickup_days;

    const ref = this.getConnection();
    return ref
      .add(requestQuery)
      .then(async (notificationsDocRef) => {
        // 更新後のデータ取得
        const notification_id = notificationsDocRef.id;
        const data = await this.getDocument(notification_id);

        // サーバー側のタイムスタンプで当日日付に更新
        const { created_at, scheduled_time, can_order_time, last_pickup_time } = data;
        const serverTimeStamp = created_at.toDate() as Date;
        const scheduledTime = copyDateOnlyYYYYMMDD(
          scheduled_time.toDate() as Date,
          serverTimeStamp,
        );
        const canOrderTime = copyDateOnlyYYYYMMDD(can_order_time.toDate() as Date, serverTimeStamp);
        const lastPickupTime = copyDateOnlyYYYYMMDD(
          last_pickup_time.toDate() as Date,
          serverTimeStamp,
        );

        // notificationsを再度更新
        await notificationsDocRef.update({
          id: notification_id,
          can_order_time: canOrderTime,
          last_pickup_time: lastPickupTime,
        });

        // order_breads側も更新、ついでにテンプレート作成用の値も取得
        const notificationTemplateBakedBreads = await Promise.all(
          orderBreads.map(async (v) => {
            const orderBread = {
              bread_id: v.bread_id,
              shop_id: v.shop_id,
              shop_name: v.shop_name,
              shop_address: v.shop_address,
              image: v.image,
              name: v.name,
              description: v.description,
              can_order_time: canOrderTime,
              last_pickup_time: lastPickupTime,
              price: v.price,
              stock: v.stock,
              maximum_order: v.maximum_order,
              width: v.width,
              depth: v.depth,
              height: v.height,
              best_before: v.best_before,
              is_reservation: v.is_reservation,
              stats: v.stats,
              notifications_ref: notificationsDocRef,
            };
            const res = await OrderBreadsDB.createByNotification(orderBread);
            const {
              data: { ref: orderBreadRef },
            } = res;

            orderBreadRefs.push(orderBreadRef);

            return {
              selected: true,
              ref: BakedBreadsDB.getDocumentRef(v.bread_id),
              maximum_order: v.maximum_order,
              stock: v.stock,
            } as NotificationTemplateBakedBreadEntity;
          }),
        );

        // notification_templatesに設定をコピー
        if (needs_template && notificationTemplateBakedBreads.length) {
          await NotificationTemplatesDB.create({
            ...notificationTemplateInitialEntity,
            name: template_title,
            pickup_supplement: query.pickup_supplement,
            scheduled_time: scheduledTime,
            can_order_time: canOrderTime,
            last_pickup_time: lastPickupTime,
            shop_id: query.shop_id,
            baked_breads: notificationTemplateBakedBreads,
          });
        }

        // 作成されたorder_breadsのrefsをnotification側に紐付ける
        if (orderBreadRefs.length) {
          notificationsDocRef.update({ order_breads: orderBreadRefs });
        }

        return this.formatSuccess({
          id: notification_id,
        });
      })
      .catch((err) => {
        console.error(err);
        throw new InternalError(err.message);
      });
  }

  // テンプレートのデータを流用して通知を作成する
  async createByTemplates(templates: NotificationTemplateEntity[]) {
    const self = this;
    // 通知時刻が早すぎるデータを除外
    const filteredTemplates = templates.filter(({ scheduled_time }) => {
      const time = copyTimeOnlyHHmmss(formatDateToDate(scheduled_time));
      const deadline = dayjs(new Date()).add(3, 'minute').toDate();
      return dayjs(time).isBefore(deadline);
    });
    return Promise.all(
      filteredTemplates.map(async (template) => {
        const shop = await ShopsDB.getDocumentRef(template.shop_id)
          .get()
          .then(async (doc) => (await doc.data()) as ShopEntity);

        const orderBreads = await Promise.all(
          template.baked_breads.map(async (v) => {
            const bakedBread = (await (await v.ref.get()).data()) as BakedBreadEntity;
            return {
              ...orderBreadInitialEntity,
              selected: true,
              bread_id: v.ref.id,
              shop_id: template.shop_id,
              shop_name: shop.name,
              shop_address: shop.address,
              image: bakedBread.image,
              name: bakedBread.name,
              price: bakedBread.price,
              description: bakedBread.description,
              scheduled_time: template.scheduled_time,
              can_order_time: template.can_order_time,
              last_pickup_time: template.last_pickup_time,
              stock: v.stock,
              maximum_order: v.maximum_order,
            } as OrderBreadEntity;
          }),
        );

        return {
          ...notificationInitialEntity,
          needs_template: false,
          is_published: true,
          template_title: '',
          shop_id: template.shop_id,
          shop_name: shop.name,
          shop_address: shop.address,
          published_time: template.scheduled_time, // 擬似的に同じ値を入れる
          scheduled_time: template.scheduled_time,
          can_order_time: template.can_order_time,
          last_pickup_time: template.last_pickup_time,
          pickup_supplement: template.pickup_supplement,
          order_breads: orderBreads,
        } as NotificationEntity;
      }),
    )
      .then((notifications) =>
        Promise.all(
          notifications.map(async (notification) => {
            console.log(notification);
            await self.create(notification);
            return notification;
          }),
        ),
      )
      .then((notifications) => {
        if (!notifications.length) {
          toaster('error', 'お知らせを作成できませんでした。お知らせ時刻を再度確認してください。');
        } else if (filteredTemplates.length !== notifications.length) {
          toaster(
            'info',
            '一部のお知らせを作成できませんでした。お知らせ時刻を再度確認してください。',
          );
        }
        return self.formatSuccess({
          notifications,
        });
      })
      .catch((err) => {
        console.log(err);
        throw new InternalError(err.message);
      });
  }

  // 予約通知を作成する
  async createReservation(payload: {
    notification: NotificationEntity;
    orderBread: OrderBreadEntity;
  }) {
    const { notification, orderBread } = payload;
    const {
      published_time,
      scheduled_time,
      can_order_time,
      last_pickup_time,
      last_pickup_days,
      is_draft,
    } = notification;
    const lastPickupDays = () => {
      const values = last_pickup_days as (Date | undefined)[];
      const filtered = values.filter((v) => v);
      return sort(filtered).by([{ asc: (v) => v }]);
    };
    const filteredQuery = this.filterQuery(notification);

    const requestQuery = {
      ...filteredQuery,
      published_time: published_time || null,
      scheduled_time: scheduled_time || null,
      can_order_time: can_order_time || null,
      last_pickup_time: null,
      last_pickup_days: lastPickupDays(),
      notified_at: null, // Firebase側で日付のundefinedは禁止されているため
      created_at: this.getServerTimeStamp(),
      updated_at: this.getServerTimeStamp(),
    };

    // notificationに保存させたくないキーを削除（setSchemaKeysでうまく制御できなかったので...）
    delete requestQuery.needs_template;
    delete requestQuery.template_title;

    const ref = this.getConnection();
    return await ref
      .add(requestQuery)
      .then(async (notificationsDocRef) => {
        // 更新後のデータ取得
        const notification_id = notificationsDocRef.id;

        // order_breads側も更新
        const res = await OrderBreadsDB.createByNotification({
          ...orderBread,
          can_order_time,
          last_pickup_time,
          notifications_ref: notificationsDocRef,
        });
        const {
          data: { ref: orderBreadRef },
        } = res;

        // 作成されたorder_breadsのrefをnotification側に紐付ける
        if (orderBreadRef) {
          await notificationsDocRef.update({
            id: notification_id,
            order_breads: [orderBreadRef],
          });
        }

        return this.formatSuccess({
          id: notification_id,
          is_draft,
        });
      })
      .catch((err) => {
        console.error(err);
        throw new InternalError(err.message);
      });
  }

  // 予約通知を更新する
  async updateReservation(payload: {
    notification: NotificationEntity;
    orderBread: OrderBreadEntity;
  }) {
    const { notification, orderBread } = payload;
    const {
      id,
      published_time,
      scheduled_time,
      can_order_time,
      last_pickup_days,
      is_draft,
    } = notification;
    const lastPickupDays = () => {
      const values = last_pickup_days as (Date | undefined)[];
      const filtered = values.filter((v) => v);
      return sort(filtered).by([{ asc: (v) => v }]);
    };
    const filteredQuery = this.filterQuery(notification);

    const requestQuery = {
      ...filteredQuery,
      published_time: published_time || null,
      scheduled_time: scheduled_time || null,
      can_order_time: can_order_time || null,
      last_pickup_time: null,
      last_pickup_days: lastPickupDays(),
      updated_at: this.getServerTimeStamp(),
    };

    // notificationに保存させたくないキーを削除（setSchemaKeysでうまく制御できなかったので...）
    delete requestQuery.needs_template;
    delete requestQuery.template_title;

    // 更新対象外
    delete requestQuery.order_breads;

    const ref = this.getDocumentRef(id);
    return await ref
      .update(requestQuery)
      .then(async () => {
        // order_breads側も更新
        await OrderBreadsDB.update(orderBread);

        return this.formatSuccess({
          id,
          is_draft,
        });
      })
      .catch((err) => {
        console.error(err);
        throw new InternalError(err.message);
      });
  }

  // 予約通知をキャンセルする（下書き状態に戻す）
  async cancelReservation(payload: { id: string }) {
    const { id } = payload;
    const ref = this.getDocumentRef(id);
    return await ref
      .get()
      .then(async (doc) => {
        const data = (await doc.data()) as NotificationEntity;
        const { is_notified, is_published, published_time, scheduled_time } = data;

        if (is_notified || is_published) {
          const msg = 'この予約は既に配信済みのため、キャンセルできません。';
          throw new InternalError(msg);
        }

        if (published_time && !isDeletableScheduledTime(published_time)) {
          const msg = 'まもなく配信されるデータのため、削除できません。';
          throw new InternalError(msg);
        }

        if (scheduled_time && !isDeletableScheduledTime(scheduled_time)) {
          const msg = 'まもなく配信されるデータのため、削除できません。';
          throw new InternalError(msg);
        }

        // 下書き状態に戻す
        await doc.ref.update({ is_draft: true });

        return this.formatSuccess({
          ...data,
          is_draft: true,
        });
      })
      .catch((err) => {
        console.error(err);
        throw new InternalError(err.message);
      });
  }

  // 予約通知を紐づくデータごとを削除する
  async deleteReservation(id: string) {
    const notificationRef = this.getDocumentRef(id);
    const notification = await notificationRef
      .get()
      .then(async (doc) => (await doc.data()) as NotificationEntity)
      .catch((err) => {
        console.error(err);
        throw new InternalError(err.message);
      });

    const { order_breads } = notification;

    // 予約商品に紐づく明細やマスタ系のデータを削除
    if (order_breads.length) {
      const orderBreadRef = OrderBreadsDB.getDocumentRef(order_breads[0].id);
      const orderBread = await orderBreadRef
        .get()
        .then(async (doc) => (await doc.data()) as OrderBreadEntity)
        .catch((err) => {
          console.error(err);
          throw new InternalError(err.message);
        });
      const { bread_id, shop_id } = orderBread;

      const bakedBreadRef = BakedBreadsDB.getDocumentRef(bread_id);

      // 店舗情報からの紐付けを削除
      await ShopsDB.removeBakedBread(shop_id, bakedBreadRef);

      // baked_breads（パンマスタ）を削除
      await bakedBreadRef.delete().catch((err) => {
        console.error(err);
        throw new InternalError(err.message);
      });

      // 念のために注文データも削除しておく
      await OrdersDB.getConnection()
        .where('notifications_ref', '==', notificationRef)
        .get()
        .then(async (snapshot) => {
          await Promise.all(
            snapshot.docs.map(async (doc) => {
              await doc.ref.delete();
            }),
          );
        })
        .catch((err) => {
          console.error(err);
          throw new InternalError(err.message);
        });

      // order_breads（通知明細）を削除
      await orderBreadRef.delete().catch((err) => {
        console.error(err);
        throw new InternalError(err.message);
      });
    }

    await notificationRef.delete().catch((err) => {
      console.error(err);
      throw new InternalError(err.message);
    });

    return this.formatSuccess(notification);
  }

  // 通知に紐づく明細（order_breads）を削除する
  async removeOrderBread(orderBreadId: string) {
    const orderBreadDocRef = await OrderBreadsDB.getDocumentRef(orderBreadId);
    const orderBread = (await orderBreadDocRef.get()).data() as OrderBreadEntity;

    return await this.getConnection()
      .where('order_breads', 'array-contains', orderBreadDocRef)
      .get()
      .then(async (snapshot) => {
        if (snapshot.empty) {
          throw new ReferenceError('削除対象の通知がありません。');
        }
        await Promise.all(
          snapshot.docs.map(async (doc) => {
            const notification = await doc.data();
            const { order_breads, scheduled_time, is_notified } = notification;

            if (is_notified) {
              const msg = 'この通知は既に配信済みのため、削除できません。';
              throw new InternalError(msg);
            }

            const scheduledTime = scheduled_time.toDate() as Date;
            if (!isDeletableScheduledTime(scheduledTime)) {
              const msg = 'お知らせ時刻が削除可能な時刻を過ぎているため、削除できません。';
              throw new InternalError(msg);
            }

            const refs = order_breads as firestore.DocumentReference[];
            const isLastOne = refs.length === 1;
            if (isLastOne) {
              await doc.ref.delete();
            } else {
              await doc.ref.update({
                order_breads: firestore.FieldValue.arrayRemove(orderBreadDocRef),
              });
            }
            await orderBreadDocRef.delete();
            return notification;
          }),
        );
        return this.formatSuccess(orderBread);
      })
      .catch((err) => {
        toaster('error', err.message);
        return this.throwError(500, err.message);
      });
  }

  formatStats = async (data: firebase.firestore.DocumentData) => {
    const userCount = await UsersDB.getRegisterdBakedBreadsUserCount(data.id);
    return {
      userCount: userCount,
    };
  };
}

export default new NotificationsDB({
  schema: 'notifications',
});
